import { Version } from "@/v2/entities/release/Release.model";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { Instruction as OldInstruction } from "@/routes/simulator/types";
import { TemporaryPolicyOverride } from "./SimulatorWrapper";

export const newReleasePolicyToInstructionJson = (
  release: Version,
  topics: Topic[],
  topicId?: number
): OldInstruction[] => {
  const instructions: OldInstruction[] = [];
  const seenInstructions = new Set<string>();

  const addUniqueInstruction = (instruction: OldInstruction) => {
    const key = `${instruction.topic}-${instruction.subTopic}-${instruction.type}-${instruction.content}`;
    if (!seenInstructions.has(key)) {
      seenInstructions.add(key);
      instructions.push(instruction);
    }
  };

  if (release.includeLink !== undefined) {
    addUniqueInstruction({
      content: release.includeLink.toString(),
      subTopic: "",
      topic: "includeLink",
      type: "general",
    });
  }

  if (release.strictMode !== undefined) {
    addUniqueInstruction({
      content: release.strictMode.toString(),
      subTopic: "",
      topic: "strictMode",
      type: "general",
    });
  }

  if (release.hideSourceUrls !== undefined) {
    addUniqueInstruction({
      content: release.hideSourceUrls.toString(),
      subTopic: "",
      topic: "hideSourceUrls",
      type: "general",
    });
  }

  // Only sent when on, so release policies of agents that don't use it stay unchanged.
  if (release.articleLinksOnRequest) {
    addUniqueInstruction({
      content: "true",
      subTopic: "",
      topic: "articleLinksOnRequest",
      type: "general",
    });
  }

  if (release.maxBackAndForth !== undefined) {
    addUniqueInstruction({
      content: release.maxBackAndForth.toString(),
      subTopic: "",
      topic: "maxBackAndForth",
      type: "general",
    });
  }

  if (release.lengthOfConversation) {
    addUniqueInstruction({
      content: release.lengthOfConversation,
      subTopic: "",
      topic: "length",
      type: "general",
    });
  }

  if (release.includeOpening !== undefined) {
    addUniqueInstruction({
      content: release.includeOpening.toString(),
      subTopic: "",
      topic: "includeOpening",
      type: "general",
    });
  }

  if (release.openingLine) {
    addUniqueInstruction({
      content: release.openingLine,
      subTopic: "",
      topic: "openingLine",
      type: "general",
    });
  }

  if (release.closingLine) {
    addUniqueInstruction({
      content: release.closingLine,
      subTopic: "",
      topic: "closingLine",
      type: "general",
    });
  }

  if (release.knowledgeBrandId) {
    addUniqueInstruction({
      content: release.knowledgeBrandId.toString(),
      subTopic: "",
      topic: "brand",
      type: "general",
    });
  }

  if (release.globalInstructions && release.globalInstructions.length > 0) {
    release.globalInstructions.forEach((globalInstruction) => {
      addUniqueInstruction({
        content: globalInstruction.content,
        subTopic: "",
        topic: "",
        type: "global-instructions",
      });
    });
  }

  if (!release.topicReleasePolicies) {
    return instructions;
  }

  const topicMap = new Map(topics.map((topic) => [topic.id, topic.topic]));

  const policiesToProcess = topicId
    ? release.topicReleasePolicies?.filter(
        (policy) => policy.topicId === topicId
      ) || []
    : release.topicReleasePolicies || [];

  policiesToProcess.forEach((topicPolicy) => {
    const topicName =
      topicMap.get(topicPolicy.topicId) || `topic_${topicPolicy.topicId}`;

    if (topicPolicy.isEnabled !== undefined) {
      addUniqueInstruction({
        content: topicPolicy.isEnabled.toString(),
        subTopic: "null",
        topic: topicName,
        type: "isEnabled",
      });
    }

    if (topicPolicy.brief !== undefined) {
      addUniqueInstruction({
        content: topicPolicy.brief || "",
        subTopic: "null",
        topic: topicName,
        type: "brief",
      });
    }

    addUniqueInstruction({
      content: (topicPolicy.isKnowledgeEnabled ?? true).toString(),
      subTopic: "knowledge",
      topic: topicName,
      type: "brief",
    });

    addUniqueInstruction({
      content: (topicPolicy.isWikiEnabled ?? false).toString(),
      subTopic: "wiki",
      topic: topicName,
      type: "brief",
    });

    addUniqueInstruction({
      content: (topicPolicy.isHistoricalTicketsEnabled ?? true).toString(),
      subTopic: "tickets",
      topic: topicName,
      type: "brief",
    });

    if (
      topicPolicy.overrideStrictMode !== undefined &&
      topicPolicy.overrideStrictMode !== null &&
      topicPolicy.overrideStrictMode !== release.strictMode
    ) {
      addUniqueInstruction({
        content: topicPolicy.overrideStrictMode.toString(),
        subTopic: "strictMode",
        topic: topicName,
        type: "brief",
      });
    }

    if (
      topicPolicy.overrideMaxBackAndForth !== undefined &&
      topicPolicy.overrideMaxBackAndForth !== null &&
      topicPolicy.overrideMaxBackAndForth !== release.maxBackAndForth
    ) {
      addUniqueInstruction({
        content: topicPolicy.overrideMaxBackAndForth.toString(),
        subTopic: "maxBackAndForth",
        topic: topicName,
        type: "brief",
      });
    }

    if (topicPolicy.instructions && topicPolicy.instructions.length > 0) {
      topicPolicy.instructions.forEach((instruction) => {
        addUniqueInstruction({
          content: instruction.content,
          subTopic: "null",
          topic: topicName,
          type: "manual",
        });
      });
    }

    if (topicPolicy.prerequisites && topicPolicy.prerequisites.length > 0) {
      topicPolicy.prerequisites.forEach((prerequisite) => {
        addUniqueInstruction({
          content: prerequisite.content,
          subTopic: "null",
          topic: topicName,
          type: "prerequisite",
        });
      });
    }
  });

  return instructions;
};

export const applyTemporarySuggestionToPolicy = (
  baseInstructions: OldInstruction[],
  override: TemporaryPolicyOverride,
  topics: Topic[]
): OldInstruction[] => {
  const topicName = topics.find((t) => t.id === override.topicId)?.topic || `topic_${override.topicId}`;

  // Create a copy of base instructions
  const instructions = [...baseInstructions];

  if (override.type === "brief_edit") {
    // Find and update the brief instruction for this topic
    const briefIndex = instructions.findIndex(
      (inst) => inst.topic === topicName && inst.type === "brief" && inst.subTopic === "null"
    );

    if (briefIndex !== -1) {
      // Update existing brief
      instructions[briefIndex] = {
        ...instructions[briefIndex],
        content: override.content,
      };
    } else {
      // Add new brief if it doesn't exist
      instructions.push({
        content: override.content,
        subTopic: "null",
        topic: topicName,
        type: "brief",
      });
    }
  } else if (override.type === "instruction_add") {
    // Add new instruction (type: "manual")
    instructions.push({
      content: override.content,
      subTopic: "null",
      topic: topicName,
      type: "manual",
    });
  } else if (override.type === "prerequisite_add") {
    // Add new prerequisite (type: "prerequisite")
    instructions.push({
      content: override.content,
      subTopic: "null",
      topic: topicName,
      type: "prerequisite",
    });
  }

  return instructions;
};
